<template>
  <div class="generate-passports">
    <!-- Проверка авторизации -->
    <div v-if="!isAdmin" class="row">
      <div class="col-lg-8 mx-auto">
        <div class="card shadow">
          <div class="card-header bg-danger text-white">
            <h3 class="mb-0">
              <i class="bi bi-shield-exclamation"></i>
              Доступ запрещен
            </h3>
          </div>
          <div class="card-body p-4 text-center">
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle"></i>
              <strong>Требуется авторизация администратора</strong>
              <p class="mb-0 mt-2">Для доступа к генерации паспортов необходимо войти как администратор.</p>
            </div>
            <button @click="requestAdminAccess" class="btn btn-primary">
              <i class="bi bi-box-arrow-in-right"></i>
              Войти как администратор
            </button>
            <router-link to="/" class="btn btn-secondary ms-2">
              <i class="bi bi-house"></i>
              На главную
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Основной контент для авторизованных -->
    <div v-else class="row">
      <div class="col-lg-8 mx-auto">
        <div class="card shadow">
          <div class="card-header bg-success text-white d-flex justify-content-between align-items-center">
            <h3 class="mb-0">
              <i class="bi bi-plus-circle"></i>
              Генерация новых паспортов
            </h3>
            <span class="badge bg-light text-dark">
              <i class="bi bi-shield-check"></i> Администратор
            </span>
          </div>

          <div class="card-body p-4">
            <div class="alert alert-info">
              <i class="bi bi-info-circle"></i>
              <strong>Информация:</strong>
              Идентификатор — <code>ПРЕФИКС-XXXXC</code>: четыре знака номера в алфавите Crockford Base32
              (<code>0-9 A-Z</code> без <code>I</code>, <code>L</code>, <code>O</code>, <code>U</code> — их путают с цифрами)
              и пятый контрольный символ. Ошибка в одном знаке или перестановка соседних не откроет чужой паспорт,
              а покажет «номер введён с ошибкой». Нумерация продолжается с последнего номера серии.
              Максимальное количество за один раз: 1000 паспортов.
            </div>

            <div class="card border-primary mb-4">
              <div class="card-body">
                <label for="qr_domain" class="form-label"><strong>Домен для новых QR-кодов:</strong></label>
                <select id="qr_domain" class="form-select" v-model="qrBaseUrl">
                  <option v-for="domain in qrDomains" :key="domain.value" :value="domain.value">
                    {{ domain.label }}
                  </option>
                </select>
                <div class="form-text">
                  В QR будет записана ссылка <code>{{ qrBaseUrl }}/{{ formatExample }}</code>.
                  Уже выпущенные QR на qr-data.ru не меняются.
                </div>
              </div>
            </div>

            <form @submit.prevent="generatePassports" v-if="!generating && !generated">
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="prefix" class="form-label"><strong>Префикс серии:</strong></label>
                  <input
                    type="text"
                    class="form-control"
                    id="prefix"
                    v-model="prefix"
                    placeholder="LPG"
                    maxlength="6"
                    :class="{ 'is-invalid': !prefixValid }"
                  >
                  <div class="form-text">2–6 знаков, латинские буквы и цифры, хотя бы одна буква: <code>LPG</code>, <code>OXY</code>, <code>LP1</code>. Задаёт тип продукции.</div>
                  <div class="invalid-feedback">Префикс — 2–6 знаков, латинские буквы и цифры, хотя бы одна буква</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label"><strong>Формат идентификатора:</strong></label>
                  <div class="form-control bg-light font-monospace">{{ formatExample }}</div>
                  <div class="form-text">Ёмкость серии — {{ formattedMaxCount }} номеров. Когда закончится, откроется следующий префикс.</div>
                </div>
              </div>

              <div class="mb-4">
                <label for="count" class="form-label">
                  <strong>Количество паспортов для генерации:</strong>
                </label>
                <input
                  type="number"
                  class="form-control form-control-lg"
                  id="count"
                  v-model.number="count"
                  min="1"
                  max="1000"
                  required
                  :class="{ 'is-invalid': count < 1 || count > 1000 }"
                >
                <div class="invalid-feedback">
                  Количество должно быть от 1 до 1000
                </div>
                <div class="form-text">
                  Будет создано {{ count || 0 }} новых паспортов в серии <code>{{ (prefix || '').toUpperCase() }}</code>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="serial_start" class="form-label"><strong>Стартовый серийный номер:</strong></label>
                  <input
                    type="number"
                    class="form-control"
                    id="serial_start"
                    v-model.number="serial_start"
                    placeholder="262001001"
                    min="0"
                  >
                  <div class="form-text">Серийный номер завода для 1-й ёмкости. Дальше +1 на каждую. Пусто — без номеров.</div>
                </div>
                <div class="col-md-6">
                  <label for="batch" class="form-label">Партия / бронь:</label>
                  <input
                    type="text"
                    class="form-control"
                    id="batch"
                    v-model="batch"
                    placeholder="RONNIE-2026-05"
                  >
                  <div class="form-text">Метка для последующей выгрузки пакета по партии.</div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="container_type" class="form-label">Тип емкости:</label>
                  <select 
                    class="form-control"
                    id="container_type"
                    v-model="container_type"
                  >
                    <option :value="null">Не выбран</option>
                    <option value="КЦ">Криоцилиндр (КЦ)</option>
                    <option value="Баллон">Баллон</option>
                    <option value="Моноблок">Моноблок</option>
                  </select>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="volume" class="form-label">Объем (л):</label>
                  <input 
                    type="number" 
                    class="form-control"
                    id="volume"
                    v-model.number="volume"
                    min="0"
                    step="0.1"
                    placeholder="40"
                  >
                </div>
                <div class="col-md-6">
                  <label for="pressure" class="form-label">Давление (атм):</label>
                  <input 
                    type="number" 
                    class="form-control"
                    id="pressure"
                    v-model.number="pressure"
                    min="0"
                    step="0.1"
                    placeholder="150"
                  >
                </div>
              </div>

              <div class="row mb-3" v-if="container_type === 'Моноблок'">
                <div class="col-md-6">
                  <label for="bundle_size" class="form-label">Баллонов в связке:</label>
                  <input
                    type="number"
                    class="form-control"
                    id="bundle_size"
                    v-model.number="bundle_size"
                    min="1"
                    step="1"
                    placeholder="12"
                  >
                </div>
              </div>

              <div class="row mb-3" v-if="container_type === 'КЦ'">
                <div class="col-md-6">
                  <label class="form-label">Наличие рамы:</label>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="has_frame"
                      v-model="has_frame"
                    >
                    <label class="form-check-label" for="has_frame">
                      Есть рама
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Наличие навесного испарителя:</label>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="has_evaporator"
                      v-model="has_evaporator"
                    >
                    <label class="form-check-label" for="has_evaporator">
                      Есть навесной испаритель
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="manufacturer" class="form-label">Производитель:</label>
                  <input 
                    type="text" 
                    class="form-control"
                    id="manufacturer"
                    v-model="manufacturer"
                    placeholder="Название производителя"
                  >
                </div>
                <div class="col-md-6">
                  <label for="gas" class="form-label">Газ:</label>
                  <select 
                    class="form-control"
                    id="gas"
                    v-model="gas"
                  >
                    <option :value="null">Не выбран</option>
                    <option value="Ar">Аргон (Ar)</option>
                    <option value="O2">Кислород (O2)</option>
                    <option value="N2">Азот (N2)</option>
                    <option value="CO2">Углекислота (CO2)</option>
                    <option value="He">Гелий (He)</option>
                    <option value="C3H8">Пропан (C3H8)</option>
                    <option value="LPG">Пропан-бутан (LPG)</option>
                  </select>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-6">
                  <label class="form-label"><strong>Партия:</strong></label>
                  <select class="form-select" v-model="batch_id">
                    <option :value="null">— без партии —</option>
                    <option v-for="item in batches" :key="item.id" :value="item.id">
                      {{ item.code }}{{ item.title ? ' — ' + item.title : '' }}
                    </option>
                  </select>
                  <div class="form-text">
                    Созданные ёмкости сразу привяжутся к партии и возьмут её характеристики
                    и сертификат. Партии заводятся в разделе <router-link to="/batches">Партии</router-link>.
                  </div>
                </div>
              </div>

              <!-- Характеристики баллона: нужны, только если партия не выбрана -->
              <div v-if="container_type === 'Баллон' && !batch_id" class="card border-secondary mb-4">
                <div class="card-header bg-light">
                  <strong>Характеристики баллона (на всю партию)</strong>
                  <div class="form-text mb-0">
                    Из сертификата поставщика. Записываются в каждую созданную ёмкость.
                    Если выбрать партию выше, эти поля не понадобятся: характеристики возьмутся из неё.
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Модель:</label>
                      <input type="text" class="form-control" v-model="cylinder.model" placeholder="YSP23.9/10">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Материал корпуса:</label>
                      <input type="text" class="form-control" v-model="cylinder.material" placeholder="HP295">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Стандарт:</label>
                      <input type="text" class="form-control" v-model="cylinder.standard" placeholder="GB 5842">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Рабочее давление, МПа:</label>
                      <input type="number" step="0.01" class="form-control" v-model.number="cylinder.working_pressure_mpa" placeholder="2.1">
                      <div class="form-text">В поле «Давление (атм)» запишется автоматически.</div>
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Испытательное давление, МПа:</label>
                      <input type="number" step="0.01" class="form-control" v-model.number="cylinder.hydro_test_pressure_mpa" placeholder="3.2">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Давление герметичности, МПа:</label>
                      <input type="number" step="0.01" class="form-control" v-model.number="cylinder.tightness_pressure_mpa" placeholder="2.1">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Наружный диаметр, мм:</label>
                      <input type="number" step="0.1" class="form-control" v-model.number="cylinder.outer_diameter_mm" placeholder="280">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Толщина стенки, мм:</label>
                      <input type="number" step="0.01" class="form-control" v-model.number="cylinder.wall_thickness_mm" placeholder="2.1">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Заводской № партии:</label>
                      <input type="text" class="form-control" v-model="cylinder.factory_batch" placeholder="001262006">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Дата изготовления:</label>
                      <input type="text" class="form-control" v-model="cylinder.manufacture_date" placeholder="2026-03">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Лицензия на изготовление:</label>
                      <input type="text" class="form-control" v-model="cylinder.license_number" placeholder="TS2237T23-2028">
                    </div>
                    <div class="col-md-12 mb-1">
                      <label class="form-label">Термообработка:</label>
                      <input type="text" class="form-control" v-model="cylinder.heat_treatment" placeholder="Отжиг для снятия напряжений, 760±20°C, 5 мин">
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-12">
                  <label for="description" class="form-label">Описание:</label>
                  <input
                    type="text"
                    class="form-control"
                    id="description"
                    v-model="description"
                    placeholder="Дополнительная информация"
                  >
                </div>
              </div>

              <div class="text-center">
                <button
                  type="submit"
                  class="btn btn-success btn-lg"
                  :disabled="!formValid"
                >
                  <i class="bi bi-plus-circle"></i>
                  Создать {{ count || 0 }} паспортов
                </button>
              </div>
            </form>

            <!-- Процесс генерации -->
            <div v-if="generating" class="text-center">
              <div class="spinner-border text-success mb-3" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Генерация...</span>
              </div>
              <h5>Создание паспортов...</h5>
              <p class="text-muted">Пожалуйста, подождите. Генерируется {{ count }} паспортов.</p>
            </div>

            <!-- Результат генерации -->
            <div v-if="generated && generatedPassports.length" class="result-section">
              <div class="alert alert-success">
                <i class="bi bi-check-circle"></i>
                <strong>Успешно!</strong>
                Создано {{ generatedPassports.length }} новых паспортов.
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5>Созданные паспорта:</h5>
                <div>
                  <button @click="downloadPackage" class="btn btn-primary btn-sm me-2" :disabled="downloadingPackage">
                    <i class="bi bi-download"></i>
                    {{ downloadingPackage ? 'Формирование…' : 'Скачать пакет (ZIP)' }}
                  </button>
                  <button @click="downloadCSV" class="btn btn-outline-primary btn-sm me-2">
                    <i class="bi bi-download"></i>
                    Скачать CSV
                  </button>
                  <button @click="reset" class="btn btn-outline-secondary btn-sm">
                    Создать еще
                  </button>
                </div>
              </div>

              <!-- Таблица созданных паспортов -->
              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-striped table-hover">
                  <thead class="table-dark sticky-top">
                    <tr>
                      <th>#</th>
                      <th>Идентификатор</th>
                      <th>Серийный №</th>
                      <th>QR</th>
                      <th>Ссылка</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(passport, index) in previewPassports" :key="passport.identifier">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <span class="badge bg-primary">{{ passport.identifier }}</span>
                      </td>
                      <td>{{ passport.serial_number || '—' }}</td>
                      <td class="qr-cell">
                        <QrCode
                          :value="qrLink(passport.identifier)"
                          :size="96"
                          :margin="1"
                          errorCorrectionLevel="M"
                          :fileName="`qr-${passport.identifier}.png`"
                          :caption="passport.identifier"
                        />
                      </td>
                      <td>
                        <a
                          :href="qrLink(passport.identifier)"
                          class="btn btn-sm btn-outline-primary"
                          target="_blank"
                          rel="noopener"
                        >
                          Открыть
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-if="generatedPassports.length > previewPassports.length" class="text-muted small mt-2 mb-0">
                Показаны первые {{ previewPassports.length }} из {{ generatedPassports.length }}.
                Полный комплект — в кнопке «Скачать пакет (ZIP)».
              </p>

              <!-- Примеры ссылок -->
              <div class="mt-4">
                <h6>Примеры ссылок для QR-кодов:</h6>
                <div class="bg-light p-3 rounded">
                  <div v-for="passport in generatedPassports.slice(0, 3)" :key="passport.identifier" class="mb-1">
                    <small class="text-muted">
                      {{ qrLink(passport.identifier) }}
                    </small>
                  </div>
                  <small v-if="generatedPassports.length > 3" class="text-muted">
                    ... и еще {{ generatedPassports.length - 3 }} ссылок
                  </small>
                </div>
              </div>
            </div>

            <!-- Ошибка -->
            <div v-if="error" class="alert alert-danger">
              <i class="bi bi-x-circle"></i>
              <strong>Ошибка:</strong>
              {{ error }}
              <button @click="reset" class="btn btn-outline-danger btn-sm ms-2">
                Попробовать снова
              </button>
            </div>
          </div>
        </div>

        <!-- Статистика -->
        <div class="card mt-4" v-if="isAdmin">
          <div class="card-body">
            <h6>Статистика генерации:</h6>
            <div class="row text-center">
              <div class="col-md-4">
                <div class="h4 text-primary">{{ totalGenerated }}</div>
                <small class="text-muted">Всего создано в этой сессии</small>
              </div>
              <div class="col-md-4">
                <div class="h4 text-success">{{ generatedPassports.length }}</div>
                <small class="text-muted">В последней генерации</small>
              </div>
              <div class="col-md-4">
                <div class="h4 text-info">{{ formattedMaxCount }}</div>
                <small class="text-muted">Ёмкость серии {{ (prefix || '').toUpperCase() }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isAdmin, login } from '@/auth'
import axios from 'axios'
import QrCode from '../components/QrCode.vue'
import { QR_DOMAINS, DEFAULT_QR_DOMAIN, qrLink } from '../qrDomains'

// Значения по умолчанию для характеристик баллона (партия Ronnie YSP23.9/10).
function defaultCylinder() {
  return {
    model: 'YSP23.9/10',
    material: 'HP295',
    standard: 'GB 5842',
    working_pressure_mpa: 2.1,
    hydro_test_pressure_mpa: 3.2,
    tightness_pressure_mpa: 2.1,
    outer_diameter_mm: 280,
    wall_thickness_mm: 2.1,
    factory_batch: '',
    manufacture_date: '',
    license_number: 'TS2237T23-2028',
    heat_treatment: 'Отжиг для снятия напряжений, 760±20°C, 5 мин'
  }
}

export default {
  name: 'GeneratePassports',
  components: { QrCode },
  computed: {
    isAdmin() {
      return isAdmin()
    },
    // Рендерим QR только для превью: 960 canvas 1024×1024 вешают браузер.
    previewPassports() {
      return this.generatedPassports.slice(0, 50)
    },
    prefixValid() {
      return /^(?=.*[A-Za-z])[0-9A-Za-z]{2,6}$/.test(this.prefix || '')
    },
    formValid() {
      return this.count >= 1 && this.count <= 1000 && this.prefixValid
    },
    // 906 880 номеров: 32^4 минус те, у кого контрольный символ выходит за алфавит
    formattedMaxCount() {
      return (906880).toLocaleString('ru-RU')
    },
    formatExample() {
      return `${(this.prefix || 'LPG').toUpperCase()}-31N0T`
    }
  },
  mounted() {
    this.loadBatches()
    // Проверяем авторизацию при загрузке компонента
    if (!this.isAdmin) {
      // Если не авторизован, роутер уже должен был перенаправить,
      // но на всякий случай проверяем еще раз
    }
  },
  data() {
    return {
      count: 10,
      prefix: 'LPG',
      serial_start: null,
      batch: '',
      batch_id: null,
      batches: [],
      downloadingPackage: false,
      container_type: null,
      bundle_size: null,
      volume: 0,
      pressure: 0,
      has_frame: false,
      has_evaporator: false,
      manufacturer: '',
      gas: null,
      description: '',
      cylinder: defaultCylinder(),
      generating: false,
      generated: false,
      generatedPassports: [],
      error: null,
      totalGenerated: 0,
      qrDomains: QR_DOMAINS,
      qrBaseUrl: DEFAULT_QR_DOMAIN
    }
  },
  methods: {
    async loadBatches() {
      try {
        const response = await axios.get('/api/batches')
        this.batches = response.data.data || []
      } catch (error) {
        this.batches = []
      }
    },

    qrLink(identifier) {
      return qrLink(identifier, this.qrBaseUrl)
    },

    async generatePassports() {
      if (!this.formValid) {
        return
      }

      this.generating = true
      this.error = null

      try {
        const response = await axios.post('/api/generate-passports', {
          count: this.count,
          prefix: (this.prefix || '').toUpperCase(),
          serial_start: this.serial_start,
          batch: this.batch,
          container_type: this.container_type,
          bundle_size: this.container_type === 'Моноблок' ? this.bundle_size : null,
          volume: this.volume,
          pressure: this.pressure,
          has_frame: this.container_type === 'КЦ' ? this.has_frame : null,
          has_evaporator: this.container_type === 'КЦ' ? this.has_evaporator : null,
          manufacturer: this.manufacturer,
          gas: this.gas,
          description: this.description,
          batch_id: this.batch_id,
          // Характеристики пишем в ёмкости, только когда партия не выбрана:
          // иначе они перекроют партийные и мы вернёмся к копиям в каждой строке
          ...(this.container_type === 'Баллон' && !this.batch_id ? this.cylinder : {}),
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: {
            'X-Admin-Auth': localStorage.getItem('adminAuth')
          }
        })

        if (response.data.success) {
          this.generatedPassports = response.data.data
          this.generated = true
          this.totalGenerated += this.generatedPassports.length
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Произошла ошибка при генерации паспортов'
        console.error('Ошибка генерации:', error)
      } finally {
        this.generating = false
      }
    },

    reset() {
      this.generated = false
      this.generatedPassports = []
      this.error = null
      this.count = 10
      this.prefix = 'LPG'
      this.serial_start = null
      this.batch = ''
      this.container_type = null
      this.volume = 0
      this.pressure = 0
      this.has_frame = false
      this.has_evaporator = false
      this.manufacturer = ''
      this.gas = null
      this.description = ''
      this.cylinder = defaultCylinder()
      this.batch_id = null
    },

    async downloadPackage() {
      if (!this.generatedPassports.length) {
        return
      }
      this.downloadingPackage = true
      try {
        const response = await axios.post('/api/export-package', {
          identifiers: this.generatedPassports.map(p => p.identifier),
          labelsPerPdf: this.generatedPassports.length > 100 ? 100 : 0,
          baseUrl: this.qrBaseUrl,
          adminAuth: localStorage.getItem('adminAuth')
        }, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') },
          responseType: 'blob'
        })

        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `qr-package-${new Date().toISOString().split('T')[0]}.zip`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        this.error = error.response?.data?.message || 'Не удалось сформировать пакет'
        console.error('Ошибка выгрузки пакета:', error)
      } finally {
        this.downloadingPackage = false
      }
    },

    downloadCSV() {
      const headers = ['Номер', 'Идентификатор', 'Серийный №', 'Ссылка']
      const rows = this.generatedPassports.map((passport, index) => [
        index + 1,
        passport.identifier,
        passport.serial_number || '',
        this.qrLink(passport.identifier)
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `gas-cylinders-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    async requestAdminAccess() {
      if (await login()) {
        // Перезагружаем компонент для обновления состояния
        this.$forceUpdate()
      }
    }
  }
}
</script>

<style scoped>
.result-section {
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.badge {
  font-family: monospace;
}

.qr-cell {
  min-width: 160px;
}

.bi-plus-circle::before { content: "⊕"; }
.bi-info-circle::before { content: "ℹ️"; }
.bi-check-circle::before { content: "✅"; }
.bi-download::before { content: "⬇️"; }
.bi-x-circle::before { content: "❌"; }
</style>
